import React, { useMemo, useState } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { Patient } from '../../types';
import { Users, UserPlus, Search, Calendar, Clock, CheckCircle, AlertCircle, Stethoscope, Baby, FileText, ArrowRight, ClipboardCheck, BarChart3 } from 'lucide-react';
import { ReceptionServiceCards, ReceptionTab } from './ReceptionServiceCards';
import { ReceptionServiceCharts } from './ReceptionServiceCharts';
import { PatientSearch } from './PatientSearch';
import { PatientReports } from './PatientReports';
import { PatientReviewDueList } from './PatientReviewDueList';
import { ReceptionQueue } from './ReceptionQueue';
import { ActiveMaternityList } from './ActiveMaternityList';
interface Props {}
export const ReceptionDashboard: React.FC<Props> = () => {
  const {
    patients,
    addPatient,
    encounters,
    createEncounter,
    currentUser,
    getPatientEncounters,
    addMaternityFile,
    maternityFiles
  } = useData();
  const [activeTab, setActiveTab] = useState<ReceptionTab>('overview');
  const [showMaternityForm, setShowMaternityForm] = useState(false);
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    sex: 'Male' as 'Male' | 'Female',
    age: '',
    maritalStatus: 'Single' as 'Married' | 'Single',
    address: '',
    contact: '',
    occupation: '',
    nextOfKin: '',
    nokContact: '',
    nokRelationship: '',
    allergies: '',
    hivStatus: 'Unknown' as 'Positive' | 'Negative' | 'Unknown',
    isVitalsCompleted: false
  });
  // Maternity Form State
  const [maternityForm, setMaternityForm] = useState({
    firstName: '',
    surname: '',
    otherNames: '',
    age: '',
    religion: '',
    churchAttended: '',
    residentialAddress: '',
    locationLandmark: '',
    phone: '',
    nokName: '',
    nokPhone: '',
    nokRelationship: '',
    spouseFirstName: '',
    spouseSurname: '',
    spouseOtherNames: '',
    spouseOccupation: '',
    spouseContact: ''
  });
  const handleRegister = (e: React.FormEvent) => {
    e.preventDefault();
    const newPatient = addPatient({
      ...formData,
      age: parseInt(formData.age) || 0
    });
    // Auto create encounter and send to nursing
    createEncounter(newPatient.id);
    // Reset form and show success
    setFormData({
      firstName: '',
      lastName: '',
      sex: 'Male',
      age: '',
      maritalStatus: 'Single',
      address: '',
      contact: '',
      occupation: '',
      nextOfKin: '',
      nokContact: '',
      nokRelationship: '',
      allergies: '',
      hivStatus: 'Unknown'
    });
    alert(`Patient registered successfully! BMC Number: ${newPatient.bmcFileNumber}. Sent to Nursing.`);
  };
  const handleRegisterMaternity = (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;
    const now = new Date();
    const yy = String(now.getFullYear()).slice(-2);
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const prefix = `MAT-${yy}${mm}`;
    const monthCount = maternityFiles.filter(m => m.bmcFileNumber.startsWith(prefix)).length;
    const sequence = String(monthCount + 1).padStart(3, '0');
    const bmcFileNumber = `${prefix}${sequence}`;
    addMaternityFile({
      bmcFileNumber,
      createdAt: new Date().toISOString(),
      createdBy: currentUser.id,
      stage: 'NURSING',
      patient: {
        firstName: maternityForm.firstName,
        surname: maternityForm.surname,
        otherNames: maternityForm.otherNames,
        age: maternityForm.age,
        religion: maternityForm.religion,
        churchAttended: maternityForm.churchAttended,
        residentialAddress: maternityForm.residentialAddress,
        locationLandmark: maternityForm.locationLandmark,
        phone: maternityForm.phone,
        nextOfKin: {
          name: maternityForm.nokName,
          phone: maternityForm.nokPhone,
          relationship: maternityForm.nokRelationship
        }
      },
      spouse: {
        firstName: maternityForm.spouseFirstName,
        surname: maternityForm.spouseSurname,
        otherNames: maternityForm.spouseOtherNames,
        occupation: maternityForm.spouseOccupation,
        contact: maternityForm.spouseContact
      },
      admission: {
        date: '',
        time: ''
      },
      generalExam: {
        temp: '',
        pulse: '',
        respiration: '',
        bp: '',
        oedema: '',
        anemia: '',
        cardiovascularSystem: '',
        respiratorySystem: '',
        centralNervousSystem: '',
        breasts: '',
        varicosities: '',
        vaginalDischarge: '',
        otherAbnormalities: '',
        msUrine: {
          sg: '',
          albumen: '',
          acetone: '',
          sugar: ''
        },
        hb: '',
        abo: '',
        rh: ''
      },
      presentPregnancy: {
        lmp: '',
        quickening: '',
        edd: '',
        gestationAge: '',
        ancSummaryFirstBp: '',
        ancSummaryAtWeeks: '',
        syphilisTest: {
          date: '',
          result: '',
          treatment: ''
        },
        hpTest: {
          date: '',
          result: ''
        },
        pelvicAssessment: '',
        abnormalities: ''
      },
      labourObservations: [],
      delivery: {
        laceration: '',
        anterior: '',
        posterior: '',
        none: '',
        repaired: '',
        membranes: '',
        membranesDate: '',
        membranesTime: '',
        episiotomy: '',
        episiotomyRepaired: '',
        maternalARV: '',
        arvTypes: '',
        uterineMassage: '',
        motherOnHAART: '',
        cytotec: '',
        syntometrine: '',
        firstStageBeganDate: '',
        firstStageBeganTime: '',
        placentaDelivery: '',
        completeDilationDate: '',
        completeDilationTime: '',
        placentaDeliveredDate: '',
        placentaDeliveredTime: '',
        abnormalityAtDilation: '',
        abnormalityAtDilationDesc: '',
        postpartumHemorrhage: '',
        deliveryMethod: '',
        ebl: '',
        abnormalityAtDelivery: '',
        abnormalityAtDeliveryDesc: '',
        complications: '',
        deliverySummary: []
      },
      newbornEvaluation: {
        ega: '',
        alive: '',
        neonatalDeath: '',
        causeOfDeath: '',
        apgar1: '',
        apgar5: '',
        apgar10: '',
        generalExam: '',
        congenitalMalformation: '',
        congenitalMalformationDesc: '',
        infantARVs: '',
        infantFeeding: '',
        resuscitationMethods: '',
        crownHeelLength: '',
        headCircumference: '',
        skinJaundice: '',
        skinOther: '',
        fontanelles: '',
        eyes: '',
        eyesTreatment: '',
        mouth: '',
        neck: '',
        chest: '',
        abdomen: '',
        umbilicus: '',
        genitalia: '',
        urine: '',
        anus: '',
        meconium: '',
        upperLimbs: '',
        erbParalysis: '',
        lowerLimbs: '',
        ortolaniSign: '',
        back: '',
        reflexesGrasp: '',
        reflexesMoro: '',
        reflexesSucking: '',
        progressNotes: ''
      },
      postDeliveryObservations: [],
      puerperiumNotes: []
    });
    setMaternityForm({
      firstName: '',
      surname: '',
      otherNames: '',
      age: '',
      religion: '',
      churchAttended: '',
      residentialAddress: '',
      locationLandmark: '',
      phone: '',
      nokName: '',
      nokPhone: '',
      nokRelationship: '',
      spouseFirstName: '',
      spouseSurname: '',
      spouseOtherNames: '',
      spouseOccupation: '',
      spouseContact: ''
    });
    alert(`Maternity patient registered successfully. File Number: ${bmcFileNumber}. File forwarded to Nursing.`);
    setShowMaternityForm(false);
  };
  return <div className="space-y-6" data-id="element-10195" data-mp="29jlx6c7bume4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between" data-id="element-10196" data-mp="29jud6cyqy7tj">
        <h1 className="text-3xl font-bold tracking-tight text-gray-900" data-id="element-10197" data-mp="1un3j8j7udobb">
          Reception Dashboard
        </h1>
        <nav className="flex flex-wrap gap-2" data-id="element-10198" data-mp="1zleqcm6r5kpu">
          <button onClick={() => setActiveTab('register')} className={`px-4 py-2 font-medium text-sm rounded-t-lg ${activeTab === 'register' ? 'bg-white text-blue-600 border-t-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-10199" data-mp="2naqx7felxgoz">
            <UserPlus className="w-4 h-4 inline-block mr-2 mb-1 text-blue-600" strokeWidth={2.25} aria-hidden="true" data-id="element-10200" data-mp="2ditmsavfasrb" />
            Register New
          </button>
          <button onClick={() => setActiveTab('queue')} className={`px-4 py-2 font-medium text-sm rounded-t-lg ${activeTab === 'queue' ? 'bg-white text-blue-600 border-t-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-10201" data-mp="3b01vmq8hiyzy">
            <Users className="w-4 h-4 inline-block mr-2 mb-1 text-blue-600" strokeWidth={2.25} aria-hidden="true" data-id="element-10202" data-mp="ulq2ef2yicte" />
            Queue
          </button>
          <button onClick={() => setActiveTab('reports')} className={`px-4 py-2 font-medium text-sm rounded-t-lg ${activeTab === 'reports' ? 'bg-white text-blue-600 border-t-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-10203" data-mp="14dw1s9vjfwim">
            <FileText className="w-4 h-4 inline-block mr-2 mb-1 text-blue-600" strokeWidth={2.25} aria-hidden="true" data-id="element-10204" data-mp="pnohdolz03fa" />
            Patient Reports
          </button>
          <button onClick={() => setActiveTab('reviewDue')} className={`px-4 py-2 font-medium text-sm rounded-t-lg ${activeTab === 'reviewDue' ? 'bg-white text-blue-600 border-t-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-10205" data-mp="2108vv6eq7kjm">
            <ClipboardCheck className="w-4 h-4 inline-block mr-2 mb-1 text-blue-600" strokeWidth={2.25} aria-hidden="true" data-id="element-10206" data-mp="nqdmrdikrnj9" />
            Review Due
          </button>
          <button onClick={() => setActiveTab('maternity')} className={`px-4 py-2 font-medium text-sm rounded-t-lg ${activeTab === 'maternity' ? 'bg-white text-pink-600 border-t-2 border-pink-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-10207" data-mp="dz0u4joyctc6">
            <Baby className="w-4 h-4 inline-block mr-2 mb-1 text-blue-600" strokeWidth={2.25} aria-hidden="true" data-id="element-10208" data-mp="jd14tjyf38" />
            Maternity Registration
          </button>
          <button onClick={() => setActiveTab('search')} className={`px-4 py-2 font-medium text-sm rounded-t-lg ${activeTab === 'search' ? 'bg-white text-blue-600 border-t-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-10209" data-mp="1ucrb2r167c38">
            <Search className="w-4 h-4 inline-block mr-2 mb-1 text-blue-600" strokeWidth={2.25} aria-hidden="true" data-id="element-10210" data-mp="21w09xqc1vse1" />
            Patient Search
          </button>
        </nav>
      </div>

      <ReceptionServiceCards activeTab={activeTab} onSelect={setActiveTab} data-id="element-10211" data-mp="2boxus34iltft" />

      {activeTab === 'overview' ? <ReceptionServiceCharts data-id="element-10212" data-mp="1ixnxnvpue2e8" /> : <button type="button" onClick={() => setActiveTab('overview')} className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-4 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-chrome-active" data-id="element-10213" data-mp="1iyfvld6wwc7o">
          <BarChart3 className="h-4 w-4 text-chrome-active" aria-hidden="true" data-id="element-10214" data-mp="33drcy6eh9gs1" />
          Back to dashboard charts
        </button>}

      {activeTab === 'register' && <Card title="Register New Patient" data-id="element-10215" data-mp="14b3vwqxlbp2x">
          <form onSubmit={handleRegister} className="space-y-6" data-id="element-10216" data-mp="z94vnpx5d8a7">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-10217" data-mp="1d9pe2kz5h236">
              <Input label="First Name" required value={formData.firstName} onChange={e => setFormData({
            ...formData,
            firstName: e.target.value
          })} data-id="element-10218" data-mp="1d7v2hx9rqmbr" />
              <Input label="Last Name" required value={formData.lastName} onChange={e => setFormData({
            ...formData,
            lastName: e.target.value
          })} data-id="element-10219" data-mp="ub3bt2rwd4xo" />
              <Input label="Age" type="number" required value={formData.age} onChange={e => setFormData({
            ...formData,
            age: e.target.value
          })} data-id="element-10220" data-mp="3t1mxhu002jmx" />

              <Select label="Sex" options={[{
            value: 'Male',
            label: 'Male'
          }, {
            value: 'Female',
            label: 'Female'
          }]} required value={formData.sex} onChange={e => setFormData({
            ...formData,
            sex: e.target.value as any
          })} data-id="element-10221" data-mp="1zjxtbhh3xb84" />
              <Select label="Marital Status" options={[{
            value: 'Single',
            label: 'Single'
          }, {
            value: 'Married',
            label: 'Married'
          }]} required value={formData.maritalStatus} onChange={e => setFormData({
            ...formData,
            maritalStatus: e.target.value as any
          })} data-id="element-10222" data-mp="16sxnd0z7txdt" />
              <Input label="Contact Number" required value={formData.contact} onChange={e => setFormData({
            ...formData,
            contact: e.target.value
          })} data-id="element-10223" data-mp="2a0i3ti8lnooh" />

              <Input label="Home Address" className="md:col-span-2" required value={formData.address} onChange={e => setFormData({
            ...formData,
            address: e.target.value
          })} data-id="element-10224" data-mp="plg5ttrkpt0u" />
              <Input label="Occupation" value={formData.occupation} onChange={e => setFormData({
            ...formData,
            occupation: e.target.value
          })} data-id="element-10225" data-mp="fi1114v40yer" />
            </div>

            <div className="border-t border-gray-200 pt-4" data-id="element-10226" data-mp="tb0gx7yt0umu">
              <h4 className="text-md font-medium text-gray-900 mb-4" data-id="element-10227" data-mp="y6z976n96wsj">
                Emergency Contact
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-10228" data-mp="137ve9njccozj">
                <Input label="Next of Kin Name" required value={formData.nextOfKin} onChange={e => setFormData({
              ...formData,
              nextOfKin: e.target.value
            })} data-id="element-10229" data-mp="1855ely89ql25" />
                <Input label="Relationship" required value={formData.nokRelationship} onChange={e => setFormData({
              ...formData,
              nokRelationship: e.target.value
            })} data-id="element-10230" data-mp="31you5t6gy9mj" />
                <Input label="Contact Number" required value={formData.nokContact} onChange={e => setFormData({
              ...formData,
              nokContact: e.target.value
            })} data-id="element-10231" data-mp="3kuewjhibd80y" />
              </div>
            </div>

            <div className="flex justify-end pt-4" data-id="element-10232" data-mp="7hv0qhk054zj">
              <Button type="submit" size="lg" data-id="element-10233" data-mp="3f884efibg1a">
                Register & Send to Nursing
              </Button>
            </div>
          </form>
        </Card>}

      {activeTab === 'queue' && <ReceptionQueue data-id="element-10234" data-mp="m1pix0qjedgq" />}

      {activeTab === 'reports' && <PatientReports data-id="element-10235" data-mp="r1buz6umpnny" />}

      {activeTab === 'reviewDue' && <PatientReviewDueList data-id="element-10236" data-mp="d2u81bcjsf73" />}

      {activeTab === 'search' && <PatientSearch data-id="element-10237" data-mp="d2c926yi3mvh" />}

      {activeTab === 'maternity' && !showMaternityForm && <ActiveMaternityList onRegister={() => setShowMaternityForm(true)} data-id="element-10238" data-mp="i0lzk49a5zar" />}

      {activeTab === 'maternity' && showMaternityForm && <Card title="Register Maternity Patient" action={<Button variant="outline" size="sm" onClick={() => setShowMaternityForm(false)} data-id="element-10240" data-mp="85u539azvsk7">
              Back to active patients
            </Button>} data-id="element-10239" data-mp="83r5pynycn2t">
          <form onSubmit={handleRegisterMaternity} className="space-y-6" data-id="element-10241" data-mp="3gjue66mw2fs7">
            <div data-id="element-10242" data-mp="3glx0afkx7orn">
              <h3 className="text-lg font-medium text-gray-900 mb-4 border-b pb-2" data-id="element-10243" data-mp="3lie0471hfrkk">
                Patient Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-10244" data-mp="4u81dtiwa93r">
                <Input label="First Name" required value={maternityForm.firstName} onChange={e => setMaternityForm({
              ...maternityForm,
              firstName: e.target.value
            })} data-id="element-10245" data-mp="3w1u1jc6entfv" />
                <Input label="Surname" required value={maternityForm.surname} onChange={e => setMaternityForm({
              ...maternityForm,
              surname: e.target.value
            })} data-id="element-10246" data-mp="3i3vy31tmhhf5" />
                <Input label="Other Names" value={maternityForm.otherNames} onChange={e => setMaternityForm({
              ...maternityForm,
              otherNames: e.target.value
            })} data-id="element-10247" data-mp="2zbl1fppkafph" />
                <Input label="Age" type="number" required value={maternityForm.age} onChange={e => setMaternityForm({
              ...maternityForm,
              age: e.target.value
            })} data-id="element-10248" data-mp="1erfvsed5cphl" />
                <Input label="Phone Number" required value={maternityForm.phone} onChange={e => setMaternityForm({
              ...maternityForm,
              phone: e.target.value
            })} data-id="element-10249" data-mp="w3ucau9m44zz" />
                <Input label="Religion" value={maternityForm.religion} onChange={e => setMaternityForm({
              ...maternityForm,
              religion: e.target.value
            })} data-id="element-10250" data-mp="cesc7fxdj3gr" />
                <Input label="Church Attended" value={maternityForm.churchAttended} onChange={e => setMaternityForm({
              ...maternityForm,
              churchAttended: e.target.value
            })} data-id="element-10251" data-mp="3oz6h2huxgal6" />
                <div className="md:col-span-2" data-id="element-10252" data-mp="317ragcw4b744">
                  <Input label="Residential Address" required value={maternityForm.residentialAddress} onChange={e => setMaternityForm({
                ...maternityForm,
                residentialAddress: e.target.value
              })} data-id="element-10253" data-mp="368onyc66ncza" />
                </div>
                <div className="md:col-span-3" data-id="element-10254" data-mp="2azbeumnlzyoi">
                  <Input label="Location / Landmark" value={maternityForm.locationLandmark} onChange={e => setMaternityForm({
                ...maternityForm,
                locationLandmark: e.target.value
              })} data-id="element-10255" data-mp="261050bxbdo6a" />
                </div>
              </div>
            </div>

            <div data-id="element-10256" data-mp="1n9h1m3zdo83q">
              <h3 className="text-lg font-medium text-gray-900 mb-4 border-b pb-2" data-id="element-10257" data-mp="1n8xtfezj4y2w">
                Next of Kin
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-10258" data-mp="1dc2z78aahjuf">
                <Input label="Name" required value={maternityForm.nokName} onChange={e => setMaternityForm({
              ...maternityForm,
              nokName: e.target.value
            })} data-id="element-10259" data-mp="1ddw4ok961y0u" />
                <Input label="Phone" required value={maternityForm.nokPhone} onChange={e => setMaternityForm({
              ...maternityForm,
              nokPhone: e.target.value
            })} data-id="element-10260" data-mp="ypvv60uvlguw" />
                <Input label="Relationship" required value={maternityForm.nokRelationship} onChange={e => setMaternityForm({
              ...maternityForm,
              nokRelationship: e.target.value
            })} data-id="element-10261" data-mp="1dgd3m45xh71a" />
              </div>
            </div>

            <div data-id="element-10262" data-mp="1wbbasis389py">
              <h3 className="text-lg font-medium text-gray-900 mb-4 border-b pb-2" data-id="element-10263" data-mp="213l0bxpaj7rp">
                Spouse Details (Optional)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-10264" data-mp="3lsozry1e0a0o">
                <Input label="First Name" value={maternityForm.spouseFirstName} onChange={e => setMaternityForm({
              ...maternityForm,
              spouseFirstName: e.target.value
            })} data-id="element-10265" data-mp="3gvyk3bthvgfs" />
                <Input label="Surname" value={maternityForm.spouseSurname} onChange={e => setMaternityForm({
              ...maternityForm,
              spouseSurname: e.target.value
            })} data-id="element-10266" data-mp="32xi4swhkghm1" />
                <Input label="Other Names" value={maternityForm.spouseOtherNames} onChange={e => setMaternityForm({
              ...maternityForm,
              spouseOtherNames: e.target.value
            })} data-id="element-10267" data-mp="r5bqyxawjkqv" />
                <Input label="Occupation" value={maternityForm.spouseOccupation} onChange={e => setMaternityForm({
              ...maternityForm,
              spouseOccupation: e.target.value
            })} data-id="element-10268" data-mp="1mg9xnt7swea2" />
                <Input label="Contact" value={maternityForm.spouseContact} onChange={e => setMaternityForm({
              ...maternityForm,
              spouseContact: e.target.value
            })} data-id="element-10269" data-mp="25edt5kurmete" />
              </div>
            </div>

            <div className="flex justify-end pt-4" data-id="element-10270" data-mp="2k1yyllf44q4h">
              <Button type="submit" size="lg" className="bg-pink-600 hover:bg-pink-700 text-white" data-id="element-10271" data-mp="2o66m5f9u4014">
                Register & Forward to Nursing
              </Button>
            </div>
          </form>
        </Card>}
    </div>;
};